import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { 
  TextInputField,
  NumberInputField,
  PriceInputField,
  SelectField,
  TextareaField,
  FormSubmitButton,
  roleOptions,
  productStatusOptions 
} from '@/components/forms/FormComponents'
import { createProductSchema, type CreateProductData } from '@/lib/form-schemas'
import { toastHelpers } from '@/lib/toast-helpers'

const categoryOptions = [
  { value: '1', label: 'Entradas' },
  { value: '2', label: 'Plato fuerte' },
  { value: '3', label: 'Postres' },
  { value: '4', label: 'Bebidas' },
]

export function FormDemo() {
  const form = useForm<CreateProductData>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      category_id: 1,
      image_url: '',
      status: 'active',
      preparation_time: 5,
    },
  })

  const onSubmit = (data: CreateProductData) => {
    console.log('Form submitted:', data)
    toastHelpers.success('¡Formulario demo enviado!', JSON.stringify(data, null, 2))
  }

  return (
    <div className="space-y-8 p-6">
      <div>
        <h2 className="text-2xl font-bold">Demo de componentes de formulario avanzados</h2>
        <p className="text-muted-foreground">
          Muestra de todos los componentes de formulario disponibles con validación e integración de notificaciones
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ejemplo de formulario de producto</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <TextInputField
                  control={form.control}
                  name="name"
                  label="Nombre del producto"
                  placeholder="Ingresa el nombre del producto"
                />
                
                <TextareaField
                  control={form.control}
                  name="description"
                  label="Descripción"
                  placeholder="Describe el producto..."
                  rows={3}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <PriceInputField
                    control={form.control}
                    name="price"
                    label="Precio"
                    currency="$"
                  />
                  
                  <NumberInputField
                    control={form.control}
                    name="preparation_time"
                    label="Tiempo de prep. (min)"
                    min={1}
                    max={120}
                  />
                </div>
                
                <SelectField
                  control={form.control}
                  name="category_id"
                  label="Categoría"
                  options={categoryOptions.map(cat => ({
                    value: cat.value,
                    label: cat.label
                  }))}
                />
                
                <TextInputField
                  control={form.control}
                  name="image_url"
                  label="URL de imagen"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  description="Imagen opcional del producto"
                />
                
                <SelectField
                  control={form.control}
                  name="status"
                  label="Estado"
                  options={productStatusOptions}
                />
                
                <FormSubmitButton className="w-full">
                  Crear producto
                </FormSubmitButton>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Biblioteca de componentes de formulario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <h4 className="font-medium">Componentes disponibles:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• TextInputField — entradas de texto, correo y contraseña</li>
                  <li>• NumberInputField — entradas numéricas con mín/máx</li>
                  <li>• PriceInputField — entradas de moneda con símbolo</li>
                  <li>• SelectField — selecciones desplegables</li>
                  <li>• TextareaField — entrada de texto multilínea</li>
                  <li>• SwitchField — interruptor booleano</li>
                  <li>• FormSubmitButton — botón con estados de carga</li>
                </ul>
              </div>
              
              <div className="grid gap-2">
                <h4 className="font-medium">Funciones integradas:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Validación con esquemas Zod</li>
                  <li>• Integración con notificaciones toast</li>
                  <li>• Estados de carga y manejo de errores</li>
                  <li>• Patrones de diseño responsivo</li>
                  <li>• Soporte de accesibilidad</li>
                  <li>• Seguridad de tipos con TypeScript</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Opciones específicas del POS</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Roles de usuario:</h4>
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map(role => (
                    <span key={role.value} className="text-xs px-2 py-1 bg-secondary rounded">
                      {role.label}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Estado del producto:</h4>
                <div className="flex gap-2">
                  {productStatusOptions.map(status => (
                    <span key={status.value} className="text-xs px-2 py-1 bg-secondary rounded">
                      {status.label}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
